const API_ROOT = 'https://sitebypro-server.ru/druzbamebel'

async function safeJson(response) {
  try {
    return await response.json()
  } catch (e) {
    return null
  }
}

export default {
  namespaced: true,

  state: () => ({
    createLoading: false,
    listLoading: false,
    detailsLoading: false,
    statusLoading: false,
    statsLoading: false,
    error: '',
    items: [],
    userLimit: 8,
    adminLimit: 10,
    offset: 0,
    hasMore: true,
    total: 0,
    currentOrder: null,
    didInitialLoad: false,
    currentOwnerKey: '',
    statistics: {
      total: 0,
      created: 0,
      accepted: 0,
      in_progress: 0,
      rejected: 0,
      completed: 0
    }
  }),

  getters: {
    createLoading: (state) => state.createLoading,
    listLoading: (state) => state.listLoading,
    detailsLoading: (state) => state.detailsLoading,
    statusLoading: (state) => state.statusLoading,
    statsLoading: (state) => state.statsLoading,
    error: (state) => state.error,
    items: (state) => state.items,
    hasMore: (state) => state.hasMore,
    total: (state) => state.total,
    currentOrder: (state) => state.currentOrder,
    didInitialLoad: (state) => state.didInitialLoad,
    statistics: (state) => state.statistics
  },

  mutations: {
    SET_CREATE_LOADING(state, value) {
      state.createLoading = value
    },
    SET_LIST_LOADING(state, value) {
      state.listLoading = value
    },
    SET_DETAILS_LOADING(state, value) {
      state.detailsLoading = value
    },
    SET_STATUS_LOADING(state, value) {
      state.statusLoading = value
    },
    SET_STATS_LOADING(state, value) {
      state.statsLoading = value
    },
    SET_ERROR(state, value) {
      state.error = value
    },
    SET_CURRENT_OWNER_KEY(state, value) {
      state.currentOwnerKey = value
    },
    RESET_LIST(state) {
      state.items = []
      state.offset = 0
      state.hasMore = true
      state.total = 0
      state.currentOrder = null
    },
    APPEND_ITEMS(state, items) {
      const existingNumbers = new Set(state.items.map((item) => item.order_number))

      items.forEach((item) => {
        if (!existingNumbers.has(item.order_number)) {
          state.items.push(item)
          existingNumbers.add(item.order_number)
        }
      })
    },
    PREPEND_ORDER(state, order) {
      const index = state.items.findIndex((item) => item.order_number === order.order_number)

      if (index !== -1) {
        state.items.splice(index, 1)
      } else {
        state.total += 1
      }

      state.items.unshift(order)
    },
    SET_PAGINATION(state, payload) {
      state.offset = Number(payload.next_offset || 0)
      state.hasMore = Boolean(payload.has_more)
      state.total = Number(payload.total || 0)
    },
    SET_CURRENT_ORDER(state, order) {
      state.currentOrder = order
    },
    SET_DID_INITIAL_LOAD(state, value) {
      state.didInitialLoad = value
    },
    SET_STATISTICS(state, payload) {
      state.statistics = {
        total: Number(payload?.total || 0),
        created: Number(payload?.created || 0),
        accepted: Number(payload?.accepted || 0),
        in_progress: Number(payload?.in_progress || 0),
        rejected: Number(payload?.rejected || 0),
        completed: Number(payload?.completed || 0)
      }
    },
    UPSERT_ORDER(state, order) {
      const index = state.items.findIndex((item) => item.order_number === order.order_number)

      if (index === -1) {
        state.items.unshift(order)
        state.total += 1
        return
      }

      state.items.splice(index, 1, order)
    },
    REMOVE_ORDER_CACHE(state) {
      state.items = []
      state.offset = 0
      state.hasMore = true
      state.total = 0
      state.currentOrder = null
      state.didInitialLoad = false
      state.currentOwnerKey = ''
      state.statistics = {
        total: 0,
        created: 0,
        accepted: 0,
        in_progress: 0,
        rejected: 0,
        completed: 0
      }
    }
  },

  actions: {
    async createOrder({ commit, rootGetters }, payload) {
      commit('SET_CREATE_LOADING', true)
      commit('SET_ERROR', '')

      try {
        const user = rootGetters['auth/user']

        if (!user?.hash) {
          const message = 'Пользователь не авторизован'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const formData = new FormData()
        formData.append('hash', user.hash)
        formData.append('product_name', payload.product_name)
        formData.append('headboard_pattern', payload.headboard_pattern || '')
        formData.append('fabric_name', payload.fabric_name)
        formData.append('quantity', String(payload.quantity))
        formData.append('comment', payload.comment || '')
        formData.append('deadline_date', payload.deadline_date)

        ;(payload.photos || []).forEach((file) => {
          formData.append('photos', file)
        })

        const response = await fetch(`${API_ROOT}/app/orders/create`, {
          method: 'POST',
          body: formData
        })

        const result = await safeJson(response)

        if (!result || String(result.status) !== '200') {
          const message = result?.data?.text || 'Не удалось создать заявку'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const order = result?.data?.order

        if (order) {
          commit('PREPEND_ORDER', order)
        }

        return { ok: true, order }
      } catch (error) {
        const message = 'Ошибка сети'
        commit('SET_ERROR', message)
        return { ok: false, message }
      } finally {
        commit('SET_CREATE_LOADING', false)
      }
    },

    async loadOrders({ commit, state, rootGetters }, payload = {}) {
      const reset = Boolean(payload.reset)

      if (state.listLoading) {
        return { ok: false }
      }

      commit('SET_ERROR', '')

      try {
        const user = rootGetters['auth/user']
        const isAdmin = rootGetters['auth/isAdmin']

        if (!user?.hash) {
          const message = 'Пользователь не авторизован'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const ownerKey = `${user.hash}_${isAdmin ? 'admin' : 'user'}`
        const requestLimit = isAdmin ? state.adminLimit : state.userLimit

        if (state.currentOwnerKey !== ownerKey) {
          commit('RESET_LIST')
          commit('SET_DID_INITIAL_LOAD', false)
          commit('SET_CURRENT_OWNER_KEY', ownerKey)
        }

        if (reset) {
          commit('RESET_LIST')
        }

        if (!reset && state.didInitialLoad && !state.hasMore) {
          return { ok: true, noMore: true, loadedCount: 0 }
        }

        commit('SET_LIST_LOADING', true)

        const currentOffset = reset ? 0 : state.offset
        const endpoint = isAdmin ? '/app/orders/admin/list' : '/app/orders/list'

        const response = await fetch(`${API_ROOT}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            hash: user.hash,
            limit: requestLimit,
            offset: currentOffset
          })
        })

        const result = await safeJson(response)

        if (!result || String(result.status) !== '200') {
          const message = result?.data?.text || 'Не удалось получить заявки'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const data = result.data || {}
        const items = Array.isArray(data.items) ? data.items : []

        commit('APPEND_ITEMS', items)
        commit('SET_PAGINATION', {
          next_offset: Number(data.next_offset || currentOffset),
          has_more: Boolean(data.has_more),
          total: Number(data.total || 0)
        })
        commit('SET_DID_INITIAL_LOAD', true)

        return {
          ok: true,
          loadedCount: items.length,
          hasMore: Boolean(data.has_more),
          nextOffset: Number(data.next_offset || currentOffset),
          total: Number(data.total || 0)
        }
      } catch (error) {
        const message = 'Ошибка сети'
        commit('SET_ERROR', message)
        return { ok: false, message }
      } finally {
        commit('SET_LIST_LOADING', false)
      }
    },

    async refreshOrders({ dispatch, rootGetters }) {
      const result = await dispatch('loadOrders', { reset: true })

      if (rootGetters['auth/isAdmin']) {
        await dispatch('fetchStatistics')
      }

      return result
    },

    async fetchOrder({ commit, state, rootGetters }, orderNumber) {
      commit('SET_DETAILS_LOADING', true)
      commit('SET_ERROR', '')

      try {
        const cached = state.items.find((item) => item.order_number === orderNumber)
        if (cached) {
          commit('SET_CURRENT_ORDER', cached)
        }

        const user = rootGetters['auth/user']
        const isAdmin = rootGetters['auth/isAdmin']

        if (!user?.hash) {
          const message = 'Пользователь не авторизован'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const endpoint = isAdmin ? '/app/orders/admin/get' : '/app/orders/get'

        const response = await fetch(`${API_ROOT}${endpoint}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            hash: user.hash,
            order_number: orderNumber
          })
        })

        const result = await safeJson(response)

        if (!result || String(result.status) !== '200') {
          const message = result?.data?.text || 'Не удалось получить заявку'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const order = result.data
        commit('SET_CURRENT_ORDER', order)
        commit('UPSERT_ORDER', order)

        return { ok: true, order }
      } catch (error) {
        const message = 'Ошибка сети'
        commit('SET_ERROR', message)
        return { ok: false, message }
      } finally {
        commit('SET_DETAILS_LOADING', false)
      }
    },

    async updateOrderStatus({ commit, rootGetters, dispatch }, payload) {
      commit('SET_STATUS_LOADING', true)
      commit('SET_ERROR', '')

      try {
        const user = rootGetters['auth/user']

        if (!user?.hash) {
          const message = 'Пользователь не авторизован'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const response = await fetch(`${API_ROOT}/app/orders/admin/update-status`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            hash: user.hash,
            order_number: payload.order_number,
            status: payload.status
          })
        })

        const result = await safeJson(response)

        if (!result || String(result.status) !== '200') {
          const message = result?.data?.text || 'Не удалось обновить статус'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const order = result.data
        commit('SET_CURRENT_ORDER', order)
        commit('UPSERT_ORDER', order)
        await dispatch('fetchStatistics')

        return { ok: true, order }
      } catch (error) {
        const message = 'Ошибка сети'
        commit('SET_ERROR', message)
        return { ok: false, message }
      } finally {
        commit('SET_STATUS_LOADING', false)
      }
    },

    async fetchStatistics({ commit, rootGetters }) {
      if (!rootGetters['auth/isAdmin']) {
        return { ok: true }
      }

      commit('SET_STATS_LOADING', true)
      commit('SET_ERROR', '')

      try {
        const user = rootGetters['auth/user']

        if (!user?.hash) {
          const message = 'Пользователь не авторизован'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        const response = await fetch(`${API_ROOT}/app/orders/admin/stats`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            hash: user.hash
          })
        })

        const result = await safeJson(response)

        if (!result || String(result.status) !== '200') {
          const message = result?.data?.text || 'Не удалось получить статистику'
          commit('SET_ERROR', message)
          return { ok: false, message }
        }

        commit('SET_STATISTICS', result.data || {})
        return { ok: true, data: result.data }
      } catch (error) {
        const message = 'Ошибка сети'
        commit('SET_ERROR', message)
        return { ok: false, message }
      } finally {
        commit('SET_STATS_LOADING', false)
      }
    },

    clearOrdersError({ commit }) {
      commit('SET_ERROR', '')
    },

    clearCurrentOrder({ commit }) {
      commit('SET_CURRENT_ORDER', null)
    },

    clearOrdersCache({ commit }) {
      commit('REMOVE_ORDER_CACHE')
    }
  }
}